/**
 * Typed reads of Backbone's open election endpoints (ADR 0011). Every request
 * is an unauthenticated GET that sends no cookies.
 *
 * Types come straight from Backbone's public projections as type-only
 * imports, so a field added there is visible here and nothing server-side is
 * ever bundled.
 */
import { chicagoToday } from "../../lib/election-live-ballot";
import type {
  PublicCycle,
  PublicCycleSummary,
  PublicPersonPage,
  PublicRaceResponses,
} from "../../lib/public-read-projections";

export type {
  PublicCycle,
  PublicCycleSummary,
  PublicPersonPage,
  PublicRaceResponses,
  PublicRace,
  PublicRaceCandidate,
  PublicCycleRace,
  PublicCycleCandidacy,
  PublicCycleCandidacyBallot,
  PublicCandidacySection,
  PublicSeatAssignmentSection,
  PublicRunBallot,
  PublicContact,
  PublicQuestionnaireResponse,
  PublicOfficeholder,
} from "../../lib/public-read-projections";
export type { PublicResponseStatus } from "../../lib/questionnaire-status";

export type Failure = "not-found" | "unavailable";
export type Result<T> = { ok: true; data: T } | { ok: false; reason: Failure };

export interface Point {
  latitude: number;
  longitude: number;
}

export type MyBallotData =
  | { outcome: "matched"; raceSlugs: string[] }
  | { outcome: "outside-scope"; raceSlugs: [] };

export type MyBallotApiResult =
  | { ok: true; data: MyBallotData }
  | {
      ok: false;
      reason:
        | "aborted"
        | "not-found"
        | "rate-limited"
        | "map-unavailable"
        | "data-incomplete"
        | "unavailable";
      retryAfterSeconds?: number;
    };

export interface Api {
  getCycles(): Promise<Result<PublicCycleSummary[]>>;
  getCycle(slug: string): Promise<Result<PublicCycle>>;
  getRaceResponses(cycle: string, race: string): Promise<Result<PublicRaceResponses>>;
  getPerson(slug: string): Promise<Result<PublicPersonPage>>;
  getMyBallot(
    cycle: string,
    ballot: string,
    point: Point,
    signal: AbortSignal
  ): Promise<MyBallotApiResult>;
  /** Drop every cached read, so the next one asks Backbone again. */
  forget(): void;
}

interface Options {
  base: string;
  fetchImpl?: typeof fetch;
  timeoutMs?: number;
  now?: () => Date;
}

const TIMEOUT_MS = 6000;

export function createApi({ base, fetchImpl, timeoutMs = TIMEOUT_MS, now = () => new Date() }: Options): Api {
  const root = base.replace(/\/+$/, "");
  const doFetch = fetchImpl ?? ((input: RequestInfo | URL, init?: RequestInit) => fetch(input, init));
  // Results by URL until the Chicago day changes. Pending reads are shared, and a
  // failure is evicted so Retry — or the next view — asks again.
  const cache = new Map<string, Promise<Result<unknown>>>();
  let cacheDay = chicagoToday(now());

  async function attempt(url: string): Promise<Result<unknown> | "retry"> {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), timeoutMs);
    try {
      const res = await doFetch(url, {
        method: "GET",
        credentials: "omit",
        headers: { accept: "application/json" },
        signal: controller.signal,
      });
      if (res.status === 404) return { ok: false, reason: "not-found" };
      if (res.status >= 500) return "retry";
      if (!res.ok) return { ok: false, reason: "unavailable" };
      const body = (await res.json()) as { data?: unknown };
      return { ok: true, data: body.data };
    } catch {
      return "retry"; // network error, CORS refusal or timeout
    } finally {
      clearTimeout(timer);
    }
  }

  async function load(url: string): Promise<Result<unknown>> {
    const first = await attempt(url);
    if (first !== "retry") return first;
    const second = await attempt(url);
    return second === "retry" ? { ok: false, reason: "unavailable" } : second;
  }

  function get<T>(path: string): Promise<Result<T>> {
    const today = chicagoToday(now());
    if (today !== cacheDay) {
      cache.clear();
      cacheDay = today;
    }
    const url = `${root}${path}`;
    let pending = cache.get(url);
    if (!pending) {
      pending = load(url);
      cache.set(url, pending);
      pending.then((r) => {
        if (!r.ok && r.reason === "unavailable") cache.delete(url);
      });
    }
    return pending as Promise<Result<T>>;
  }

  async function getMyBallot(
    cycle: string,
    ballot: string,
    point: Point,
    signal: AbortSignal
  ): Promise<MyBallotApiResult> {
    try {
      const response = await doFetch(
        `${root}/api/public/elections/${seg(cycle)}/ballots/${seg(ballot)}/my-ballot`,
        {
          method: "POST",
          credentials: "omit",
          cache: "no-store",
          headers: {
            accept: "application/json",
            "content-type": "application/json",
          },
          body: JSON.stringify({
            latitude: point.latitude,
            longitude: point.longitude,
          }),
          signal,
        }
      );
      const envelope = (await response.json().catch(() => ({}))) as {
        data?: MyBallotData;
        error?: { code?: string; retryAfterSeconds?: number };
      };
      if (response.ok && envelope.data) {
        return { ok: true, data: envelope.data };
      }
      if (response.status === 404) return { ok: false, reason: "not-found" };
      if (response.status === 429) {
        const headerSeconds = Number(response.headers.get("retry-after"));
        const bodySeconds = envelope.error?.retryAfterSeconds;
        const retryAfterSeconds = Number.isFinite(headerSeconds) && headerSeconds > 0
          ? headerSeconds
          : typeof bodySeconds === "number" && Number.isFinite(bodySeconds)
            ? bodySeconds
            : undefined;
        return { ok: false, reason: "rate-limited", ...(retryAfterSeconds ? { retryAfterSeconds } : {}) };
      }
      if (response.status === 503 && envelope.error?.code === "MAP_UNAVAILABLE") {
        return { ok: false, reason: "map-unavailable" };
      }
      if (response.status === 503 && envelope.error?.code === "DATA_INCOMPLETE") {
        return { ok: false, reason: "data-incomplete" };
      }
      return { ok: false, reason: "unavailable" };
    } catch {
      return { ok: false, reason: signal.aborted ? "aborted" : "unavailable" };
    }
  }

  const seg = encodeURIComponent;
  return {
    getCycles: () => get("/api/public/elections"),
    getCycle: (slug) => get(`/api/public/elections/${seg(slug)}`),
    getRaceResponses: (cycle, race) => get(`/api/public/elections/${seg(cycle)}/races/${seg(race)}/responses`),
    getPerson: (slug) => get(`/api/public/people/${seg(slug)}`),
    getMyBallot,
    forget: () => cache.clear(),
  };
}
